import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import {
  enableNetwork,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore'
import { getFunctions, type Functions } from 'firebase/functions'

const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID?.trim()
const apiKey = import.meta.env.VITE_FIREBASE_API_KEY?.trim()

export const firebaseConfigured = Boolean(projectId && apiKey)

let app: FirebaseApp | null = null
let auth: Auth | null = null
let db: Firestore | null = null
let functions: Functions | null = null

if (firebaseConfigured) {
  app = initializeApp({
    apiKey,
    projectId,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN?.trim() || `${projectId}.firebaseapp.com`,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET?.trim() || undefined,
    appId: import.meta.env.VITE_FIREBASE_APP_ID?.trim() || undefined,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID?.trim() || undefined,
  })
  auth = getAuth(app)
  db = initializeFirestore(app, {
    // Persistent cache keeps previously loaded lists usable offline. Use Firestore's normal
    // realtime WebChannel transport while connected; the earlier forced long-polling workaround
    // was only masking a misconfigured project id and added unnecessary listener latency.
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  })

  // Firestore normally reconnects automatically. Explicitly kick the network when the browser
  // reports that connectivity has returned so queued shopping-list writes are retried promptly.
  window.addEventListener('online', () => {
    if (db) void enableNetwork(db).catch((error) => console.warn('Shelfway Firestore reconnect failed', error))
  })

  functions = getFunctions(app)
}

export const requireAuth = (): Auth => {
  if (!auth) throw new Error('Shelfway Firebase is not configured. Add VITE_FIREBASE_* environment variables.')
  return auth
}
export const requireDb = (): Firestore => {
  if (!db) throw new Error('Shelfway Firebase is not configured. Add VITE_FIREBASE_* environment variables.')
  return db
}
export const requireFunctions = (): Functions => {
  if (!functions) throw new Error('Shelfway Firebase is not configured.')
  return functions
}
